import { useCallback, useMemo } from 'react'
import { Spinner, SpinnerSize, MessageBar, MessageBarType, Stack, Text } from '@fluentui/react'
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { apiService } from '../../services/apiService'
import { useApi } from '../../hooks/useApi'
import type { ApplicationChangesData } from '../../types'

const COLOR_ADDED = '#107c10'      // green
const COLOR_MODIFIED = '#ffaa44'   // amber
const COLOR_REMOVED = '#a4262c'    // red
const COLOR_TOTAL = '#0078d4'      // blue

interface ApplicationChangesChartProps {
  days: number
  height?: number
  showSummary?: boolean
}

export function ApplicationChangesChart({
  days,
  height = 320,
  showSummary = true,
}: ApplicationChangesChartProps) {
  const fetchChanges = useCallback(() => apiService.getApplicationChanges(days), [days])
  const { data, loading, error } = useApi<ApplicationChangesData>(fetchChanges)

  const formatDate = (value: unknown) => {
    if (typeof value !== 'string') return String(value)
    const date = new Date(value)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const hasPoints = useMemo(() => (data?.dataPoints?.length ?? 0) > 0, [data])

  if (loading) {
    return (
      <Stack horizontalAlign="center" styles={{ root: { padding: 40 } }}>
        <Spinner size={SpinnerSize.large} />
      </Stack>
    )
  }

  if (error) {
    return <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
  }

  if (!hasPoints) {
    return (
      <Stack horizontalAlign="center" styles={{ root: { padding: 40 } }}>
        <Text variant="medium" styles={{ root: { color: '#605e5c' } }}>
          No change history available yet for the last {days} days. The daily diff job
          needs at least two consecutive snapshots before this chart populates.
        </Text>
      </Stack>
    )
  }

  return (
    <div>
      {showSummary && data && (
        <Stack horizontal tokens={{ childrenGap: 24 }} styles={{ root: { marginBottom: 16 } }}>
          <SummaryStat label="Current total" value={data.summary.currentTotal} color={COLOR_TOTAL} />
          <SummaryStat label="Added" value={data.summary.totalAdded} color={COLOR_ADDED} />
          <SummaryStat label="Modified" value={data.summary.totalModified} color={COLOR_MODIFIED} />
          <SummaryStat label="Removed" value={data.summary.totalRemoved} color={COLOR_REMOVED} />
        </Stack>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart
          data={data?.dataPoints ?? []}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
          <XAxis
            dataKey="date"
            tickFormatter={formatDate}
            tick={{ fontSize: 12, fill: '#605e5c' }}
          />
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 12, fill: '#605e5c' }}
            label={{ value: 'Daily changes', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#605e5c' } }}
            allowDecimals={false}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 12, fill: '#605e5c' }}
            label={{ value: 'Cumulative apps', angle: 90, position: 'insideRight', style: { fontSize: 12, fill: '#605e5c' } }}
            allowDecimals={false}
          />
          <Tooltip
            labelFormatter={formatDate}
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #e8e8e8',
              borderRadius: 4,
            }}
          />
          <Legend />
          <Bar yAxisId="left" dataKey="added" name="Added" fill={COLOR_ADDED} stackId="changes" />
          <Bar yAxisId="left" dataKey="modified" name="Modified" fill={COLOR_MODIFIED} stackId="changes" />
          <Bar yAxisId="left" dataKey="removed" name="Removed" fill={COLOR_REMOVED} stackId="changes" />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="cumulativeTotal"
            name="Total apps"
            stroke={COLOR_TOTAL}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 5 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}

function SummaryStat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <Stack tokens={{ childrenGap: 2 }}>
      <Text variant="small" styles={{ root: { color: '#605e5c' } }}>{label}</Text>
      <Text variant="xLarge" styles={{ root: { color, fontWeight: 600 } }}>{value}</Text>
    </Stack>
  )
}
